/**
 * Every name and timing the sub-session feature uses. It imports nothing, so any bundle
 * can take it.
 */

// Wire names (shared with IBE)

/**
 * Request and response header carrying the sub-session id. IBE omits it from a
 * response when it only resolved the sub-session through its last-used fallback.
 */
export const SUB_SESSION_HEADER = 'IBE-SubSessionId';

/** Id value asking IBE to mint a new sub-session. */
export const NEW_SUB_SESSION_ID = '-1';

/** Query parameter IBE resolves the id from on page navigations, form submissions and redirects. */
export const SUB_SESSION_QUERY_PARAM = 'ibe.ss';

/**
 * Key of `window.spa_variables` holding the id IBE resolved for the document. IBE
 * omits it when it only used its last-used fallback, so a missing value is normal.
 */
export const SUB_SESSION_SPA_VARIABLE = 'subSessionId';

/** Site property that enables the feature on the client. IBE's own `SubSession.Enabled` must be on as well. */
export const MULTI_SESSION_ENABLED_PROPERTY = 'IbeClient.MultiSession.Enabled';

// Client names (ours)

/** sessionStorage key under which a tab keeps its own sub-session id. */
export const SUB_SESSION_STORAGE_KEY = 'ibe.subSessionId';

/** sessionStorage key recording which sub-session the sales-process keys were written under. */
export const SALES_PROCESS_KEYS_OWNER_STORAGE_KEY = 'ibe.subSessionKeysOwner';

/** BroadcastChannel the tabs of one origin use to resolve who owns an id. */
export const CLAIM_CHANNEL_NAME = 'ibe.subSession';

/** Window event fired whenever this tab's sub-session id becomes known or changes. */
export const SUB_SESSION_ID_EVENT = 'ibe:subSessionId';

export type SubSessionIdEventDetail = {
  subSessionId: string;
};

/** BroadcastChannel messages between tabs. */
export const CLAIM_MESSAGE = {
  CLAIM: 'CLAIM',
  CONFLICT: 'CONFLICT',
} as const;

/** postMessage protocol between the page and the service worker, as `{ type, ...fields }`. */
export const WORKER_MESSAGE = {
  /** Worker asks a page which id it holds, with a MessagePort; answered `{ subSessionId }`. */
  WHO_ARE_YOU: 'SUB_SESSION_WHO_ARE_YOU',
  /** Worker tells a page which id IBE just minted: `{ subSessionId }`. */
  ASSIGNED: 'SUB_SESSION_ASSIGNED',
  /** Page announces its id without being asked: `{ subSessionId: string | null }`. */
  READY: 'SUB_SESSION_READY',
  /** Page asks the worker to forget it, so its next request mints a new id. */
  RESET: 'SUB_SESSION_RESET',
  /** Uncontrolled page (e.g. after a hard reload) asks the active worker to claim it. */
  TAKE_CONTROL: 'SUB_SESSION_TAKE_CONTROL',
  /** Page with the feature off tells a left-over worker to pass everything through. */
  DISABLE: 'SUB_SESSION_DISABLE',
  /** Page with the feature on tells the worker to lift a kill switch it may still hold, also a persisted one. */
  ENABLE: 'SUB_SESSION_ENABLE',
  /**
   * Worker asks a page to report one of its events through the page's log beacon:
   * `{ name, data, pathname }` with a MessagePort, answered `{ isReported: boolean }`.
   */
  TELEMETRY: 'SUB_SESSION_TELEMETRY',
} as const;

export type WorkerMessageType = (typeof WORKER_MESSAGE)[keyof typeof WORKER_MESSAGE];

/** Lifecycle events only the worker can see. It has no log beacon, so a page reports them. */
export const WORKER_SUB_SESSION_EVENT = {
  /** The worker took an id from an IBE response for one of its pages. */
  WORKER_ADOPTED: 'workerAdopted',
  /** A page never answered "who are you", so its requests pass through untouched. */
  LEGACY: 'legacy',
  /** A page with the feature off switched the worker off (kill switch). */
  DISABLED: 'disabled',
  /** The worker started again after being stopped, and found the kill switch it had persisted. */
  DISABLED_AFTER_RESTART: 'disabledAfterRestart',
} as const;

export type WorkerSubSessionEventName =
  (typeof WORKER_SUB_SESSION_EVENT)[keyof typeof WORKER_SUB_SESSION_EVENT];

/**
 * Where the worker persists the kill switch, so a restart after being idle
 * does not forget it. Cache Storage is shared by the whole origin, as the
 * worker is; it holds this one marker and nothing of any booking flow.
 */
export const WORKER_STATE_CACHE_NAME = 'ibe.subSession.worker';
export const WORKER_KILL_SWITCH_CACHE_KEY = '/static/js/subSession/kill-switch';

// Timings (defaults; tunable per site)

/** How long a tab listens for an objection after claiming an id. */
export const DEFAULT_CLAIM_WINDOW_MS = 100;

/** How long the worker waits for a page to answer before treating it as a legacy page. */
export const DEFAULT_CLIENT_ANSWER_TIMEOUT_MS = 500;

/** Proposed site properties overriding the defaults above. Not yet provided by IBE. */
export const CLAIM_WINDOW_MS_PROPERTY = 'IbeClient.MultiSession.ClaimWindowMs';
export const CLIENT_ANSWER_TIMEOUT_MS_PROPERTY = 'IbeClient.MultiSession.ClientAnswerTimeoutMs';

// Service worker location

/** Path of the worker script, relative to PUBLIC_URL. Its scope must be the whole origin. */
export const SERVICE_WORKER_PATH = '/static/js/subSession/sw.js';
export const SERVICE_WORKER_SCOPE = '/';

/** Worker script URL parameter for the tuned answer timeout: the worker cannot read properties. */
export const WORKER_ANSWER_TIMEOUT_QUERY_PARAM = 'answerTimeoutMs';
